import env from '@beam-australia/react-env';
import React from 'react';
import { NavLink } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover, {
  type PopoverOrigin,
  type PopoverReference,
} from '@mui/material/Popover';
import classNames from 'classnames';
import EditProfileModal from '@/components/editProfile/EditProfileModal';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useMobileView from '@/hooks/ui/useMobileView';
import useCurrentUser from '@/lib/hooks/useCurrentUser';
import translate from '@/lib/intl/translate';
import { userService } from '@/services/user';
import getInitials from '@/utils/getInitials';
import { showErrorMessage } from '@/utils/ToastFeedback';
import { LayoutContext } from './LayoutProvider';

const helpLinkAddress = `mailto:${env('HELP_LINK_ADDRESS')}`;
const privacyLink = env('PRIVACY_LINK');
const feedbackLink = env('FEEDBACK_LINK');

function UserProfile() {
  const { isExpanded, showHover } = React.useContext(LayoutContext);
  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const [isEditModalOpen, setEditModalOpen] = React.useState(false);
  const { user } = useCurrentUser();
  const open = Boolean(anchorEl);
  const handleOpenMenu = (event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget);

    /**
     * Blur the button, or else it will still show the focus styles.
     */
    buttonRef.current?.blur?.();
  };

  const options = React.useMemo(
    () => [
      {
        'aria-haspopup': 'dialog',
        divider: true,
        className: 'py-4 border-grey-300',
        label: translate('EDIT_PROFILE'),
        onClick: () => {
          setEditModalOpen(true);
          setAnchorEl(null);
        },
        type: 'button',
      },
      {
        divider: true,
        className: 'py-4 border-grey-300',
        label: translate('USER_MY_DOWNLOADS'),
        to: AuthRoutes.USER_DOWNLOADS,
        onClick: () => {
          setAnchorEl(null);
        },
        type: 'a',
      },
      {
        className: 'pt-4',
        label: translate('HELP_LINK'),
        href: helpLinkAddress,
        onClick: () => {
          setAnchorEl(null);
        },
        type: 'a',
      },
      {
        label: translate('SEND_FEEDBACK_LINK'),
        href: feedbackLink,
        onClick: () => {
          setAnchorEl(null);
        },
        rel: 'noopener noreferrer',
        target: '_blank',
        type: 'a',
      },
      {
        className: 'pb-4 border-grey-300',
        divider: true,
        href: privacyLink,
        label: translate('PRIVACY_LINK'),
        onClick: () => {
          setAnchorEl(null);
        },
        rel: 'noopener noreferrer',
        target: '_blank',
        type: 'a',
      },
      {
        className: 'px-3 py-4',
        label: translate('SIGN_OUT'),
        onClick: () => {
          userService.logout().then((response) => {
            if (!response) {
              showErrorMessage({ message: translate('ERROR_LOGGING_OUT') });
            }
          });
          setAnchorEl(null);
        },
        type: 'button',
      },
    ],
    [],
  );

  /**
   * Add a click event on the menu items to capture enter button clicks
   */
  const handleMenuItemKeyPress = (event: React.KeyboardEvent) => {
    const { currentTarget } = event;
    if (
      event.keyCode === 13 &&
      currentTarget.firstChild instanceof HTMLElement
    ) {
      currentTarget.firstChild.click();
      setAnchorEl(null);
    }
  };

  const isMobile = useMobileView();
  const popoverProps = React.useMemo(
    () => ({
      ...(isMobile
        ? {
            anchorReference: 'none' as PopoverReference,
          }
        : {
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            } as PopoverOrigin,
            transformOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            } as PopoverOrigin,
          }),
    }),
    [isMobile],
  );

  return (
    <>
      <Button
        aria-haspopup="true"
        aria-controls="user-profile-menu"
        aria-expanded={open ? 'true' : undefined}
        className="h-[70px] cursor-pointer justify-between rounded-none p-4 hover:bg-primary-light/5 focus:bg-black/10 focus:text-[currentColor]"
        id="user-profile-button"
        onClick={handleOpenMenu}
        ref={buttonRef}
      >
        <Avatar alt="user info" className="h-8 w-8 text-base text-primary-main">
          {getInitials(user?.displayName)}
        </Avatar>
        <span
          className={classNames(
            'ml-2 w-40 text-left text-sm font-medium text-text-primary transition-opacity duration-100 hover:text-text-primary',
            {
              'opacity-100': isExpanded || showHover,
              'opacity-0': !isExpanded && !showHover,
            },
          )}
        >
          {user?.displayName}
        </span>
      </Button>

      <Popover
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={() => setAnchorEl(null)}
        TransitionComponent={Fade}
        PaperProps={{
          className: classNames(
            'md:max-w-auto md:min-w-auto w-[calc(100vw-16px)] min-w-[300px] max-w-[calc(100vw-16px)] md:w-auto',
            {
              'bottom-8 left-2 right-2': isMobile,
            },
          ),
        }}
        {...popoverProps}
      >
        <div className="flex flex-row gap-2 border-b border-grey-300 p-3">
          <Avatar
            alt={user?.displayName}
            className="h-8 w-8 self-center text-base font-medium text-secondary-main"
          >
            {getInitials(user?.displayName)}
          </Avatar>
          <div className="self-center">
            <div className="type-body2">{user?.displayName}</div>
            <div className="text-text-secondary type-body1">{user?.email}</div>
          </div>
        </div>

        <MenuList
          aria-labelledby="user-profile-button"
          className="py-0"
          id="user-profile-menu"
        >
          {options.map(
            ({
              'aria-haspopup': ariaHaspopup,
              className,
              divider,
              href,
              label,
              onClick,
              rel,
              target,
              to,
              type,
            }) => {
              const listItemText = <ListItemText primary={label} />;
              const listItem =
                type === 'button' ? (
                  <ListItemButton
                    {...(ariaHaspopup && {
                      'aria-haspopup':
                        ariaHaspopup as React.AriaAttributes['aria-haspopup'],
                    })}
                    className={classNames(
                      'p-3 hover:bg-transparent focus-visible:bg-grey-100',
                      className,
                    )}
                    component="button"
                    focusVisibleClassName="bg-grey-100"
                    divider={divider}
                    onClick={onClick}
                  >
                    {listItemText}
                  </ListItemButton>
                ) : (
                  <ListItem
                    className={classNames(
                      'p-3 focus-visible:bg-grey-100',
                      className,
                    )}
                    divider={divider}
                    onClick={onClick}
                    {...(rel && { rel })}
                    {...(target && { target })}
                    {...(href && {
                      component: 'a',
                      href,
                    })}
                    {...(to && {
                      component: NavLink,
                      to,
                    })}
                  >
                    {listItemText}
                  </ListItem>
                );

              return (
                <MenuItem
                  key={label}
                  className="p-0 hover:cursor-pointer hover:bg-grey-100"
                  onKeyDown={handleMenuItemKeyPress}
                >
                  {listItem}
                </MenuItem>
              );
            },
          )}
        </MenuList>
      </Popover>
      <EditProfileModal
        open={isEditModalOpen}
        handleClose={() => setEditModalOpen(false)}
      />
    </>
  );
}

export default UserProfile;
